import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import { ChartType, DataWidget } from 'types/dashboard_widget';
import { Dashboard } from 'types/dashboard';
import { DataPoolName } from 'dashboards/types/data_pool';
import { DimensionInput } from 'dashboards/types/dimension';
import { Sort, SORTS } from 'types/column';
import { TimeSeriesGranularity } from 'dashboards/types/time';
import { type DataWidgetFiltersInput } from 'dashboards/types/filter';

import { DATA_POOLS } from 'dashboards/services/data_pool';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { isGranularityOptionDisabled } from 'dashboards/components/GranularitySelect/validate';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useModal } from 'hooks/useModal';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { validateDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/validate';

import { ChartSelect } from 'dashboards/components/ChartSelect/ChartSelect';
import { DataPoolColumnsSelect } from 'dashboards/components//DataPoolColumnsSelect/DataPoolColumnsSelect';
import { DataPoolSelect } from 'dashboards/components/DataPoolSelect/DataPoolSelect';
import { DataWidgetFiltersInput as DataWidgetFiltersInputComponent } from 'dashboards/components/DataWidgetFiltersInput/DataWidgetFiltersInput';
import { DimensionSelect } from 'dashboards/components/DimensionsSelect/DimensionSelect';
import { Field } from 'components/Field/Field';
import { GranularitySelect } from 'dashboards/components/GranularitySelect/GranularitySelect';
import { Input } from 'components/Input/Input';
import { MetricSelect } from 'dashboards/components/MetricSelect/MetricSelect';
import { NumberInput } from 'components/NumberInput/NumberInput';
import { Select } from 'components/Select/Select';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

const defaultDataPool: DataPoolName = 'DP_event_metadata';

const SORT_OPTIONS = Object.values(SORTS).map((value) => ({
  value,
  label: value,
}));

type DataWidgetFieldValues = {
  name: string;
  metric: string;
  dataPool: DataPoolName;
  chartType: ChartType;
  granularity: TimeSeriesGranularity;
  dimensions: DimensionInput[];
  columns: string[];
  rowLimit: number;
  orderByColumn: string;
  sort: Sort;
  filters: DataWidgetFiltersInput[];
};

type DataFormProps = { widget?: DataWidget<ChartType>; dashboardId: string };

export function DataForm({ widget, dashboardId }: DataFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);
  const { data: userMetrics } = useUserMetrics();

  const { mutate: mutateDashboard, isLoading: isUpdating } = useUpdateDashboard(
    {
      onSuccess(data) {
        resetForm(data);
        close();
      },
    }
  );

  // Intentionally using `any` to be able to set all default values no matter the chart type
  const query = widget?.query as any;

  const {
    control,
    formState: { errors, dirtyFields },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<DataWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      metric: query?.metric,
      dataPool: query?.dataPool?.name ?? defaultDataPool,
      chartType: widget?.chartType ?? 'timeseries-bar',
      granularity: query?.granularity ?? 'DAY',
      dimensions: query?.dimensions ?? [],
      columns: query?.columns ?? [],
      rowLimit: query?.rowLimit ?? 10,
      orderByColumn: query?.orderByColumn ?? 'timestamp',
      sort: query?.sort ?? 'DESC',
      filters: query?.filters,
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const metric = watch('metric');
  const chartType = watch('chartType');
  const dataPool = watch('dataPool');
  const columns = watch('columns');

  const isTimeSeries = String(chartType).includes('timeseries');
  const isLeaderboard = String(chartType).includes('leaderboard');
  const isPieChart = chartType === 'pie';
  const isGroupedChart =
    chartType === 'timeseries-bar-stacked' ||
    chartType === 'timeseries-bar-grouped' ||
    chartType === 'timeseries-heatmap';
  const isTable = chartType === 'table';

  function resetForm(updatedDashboard: Dashboard) {
    if (!widget?.id) {
      return;
    }

    const updatedWidget = updatedDashboard.configuration.widgets.find(
      ({ id }) => id === widget.id
    ) as DataWidget<ChartType>;

    if (!updatedWidget) {
      return;
    }

    // Intentionally using `any` to be able to reset all values no matter the chart type
    const updatedQuery = updatedWidget?.query as any;

    reset({
      name: updatedWidget?.name,
      metric: updatedQuery?.metric,
      dataPool: updatedQuery?.dataPool?.name,
      chartType: updatedQuery?.chartType,
      granularity: updatedQuery?.granularity,
      dimensions: updatedQuery?.dimensions,
      columns: updatedQuery?.columns,
      rowLimit: updatedQuery?.rowLimit,
      orderByColumn: updatedQuery?.orderByColumn,
      sort: updatedQuery?.sort,
      filters: updatedQuery?.filters,
    });
  }

  function handleDataPoolChange(dataPool: DataPoolName | null) {
    if (!dataPool) {
      return;
    }

    // Available columns change when dataPool changes
    // Filter out columns that don't exist on the selected dataPool
    const currentColumns = getValues('columns');
    const filteredColumns = currentColumns.filter((column) =>
      DATA_POOLS[dataPool].columns.some(({ name }) => name === column)
    );
    setValue('columns', filteredColumns);

    // Reset orderByColumn if column doesn't exist in selected dataPool
    const currentOrderByColumn = getValues('orderByColumn');
    const columnExistsInDataPool = DATA_POOLS[dataPool].columns.some(
      ({ name }) => name === currentOrderByColumn
    );

    if (!columnExistsInDataPool) {
      setValue('orderByColumn', 'timestamp');
    }
  }

  function handleMetricChange(metric: string | null) {
    if (!metric) {
      return;
    }

    if (!userMetrics) {
      setValue('dimensions', []);
      return;
    }

    const metricObject = userMetrics.data.find(({ name }) => name === metric);

    if (!metricObject) {
      setValue('dimensions', []);
      return;
    }

    // Available dimensions change when metric changes
    // Filter out dimensions that don't exist on the selected metric
    const currentDimensions = getValues('dimensions');
    const nextDimensions = metricObject.dimensions;
    const filteredDimensions = currentDimensions.filter((currentDimension) =>
      nextDimensions.some(
        (nextDimension) => currentDimension.columnName === nextDimension.name
      )
    );

    setValue('dimensions', filteredDimensions);
  }

  function onSubmit({
    name,
    chartType,
    dataPool,
    metric,
    granularity,
    rowLimit,
    orderByColumn,
    sort,
    dimensions,
    columns,
    filters: dataWidgetFilters,
  }: DataWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();
    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 4, 2);

    const filters = dataWidgetFilters?.length ? dataWidgetFilters : undefined;

    switch (chartType) {
      case 'timeseries-bar':
      case 'timeseries-line': {
        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<'timeseries-bar' | 'timeseries-line'>(
            id,
            name,
            chartType,
            { metric, granularity, filters },
            layout
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
      case 'timeseries-bar-stacked':
      case 'timeseries-bar-grouped':
      case 'timeseries-heatmap': {
        // When switching between single and multi select
        // These chart types expect only one dimension
        const [dimension] = dimensions;

        if (!dimension) {
          return;
        }

        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<
            | 'timeseries-bar-stacked'
            | 'timeseries-bar-grouped'
            | 'timeseries-heatmap'
          >(
            id,
            name,
            chartType,
            {
              metric,
              granularity,
              dimensions: [dimension],
              filters,
            },
            layout
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
      case 'pie': {
        // When switching between single and multi select
        // Pie chart expects only one dimension
        const [dimension] = dimensions;

        if (!dimension) {
          return;
        }

        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<'pie'>(
            id,
            name,
            chartType,
            {
              metric,
              rowLimit,
              sort,
              dimensions: [dimension],
              filters,
            },
            layout
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
      case 'leaderboard-bar':
      case 'leaderboard-table': {
        if (!dimensions.length) {
          return;
        }

        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<'leaderboard-bar' | 'leaderboard-table'>(
            id,
            name,
            chartType,
            { metric, rowLimit, sort, dimensions, filters },
            layout
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
      case 'table': {
        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<'table'>(
            id,
            name,
            chartType,
            {
              dataPool: { name: dataPool },
              columns,
              filters,
              orderByColumn,
              sort,
            },
            widget?.layout ||
              getNewWidgetLayout(dashboard.configuration.widgets, 4, 3)
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
      case 'counter': {
        const updatedDashboard = upsertWidget(
          dashboard,
          new DataWidget<'counter'>(
            id,
            name,
            chartType,
            { metric, filters },
            widget?.layout ||
              getNewWidgetLayout(dashboard.configuration.widgets, 2, 1)
          )
        );

        mutateDashboard({
          id: updatedDashboard.id,
          configuration: updatedDashboard.configuration,
        });
        break;
      }
    }
  }

  function getDataWidgetFilterColumns() {
    if (isTable && dataPool) {
      // Only selected columns can be filtered
      return DATA_POOLS[dataPool].columns.filter(({ name }) =>
        columns.includes(name)
      );
    }

    if (!isTable && metric) {
      return getDimensionsFromMetricsResponse(metric, userMetrics);
    }

    return [];
  }

  const dataWidgetFilterDisabled =
    (isTable && columns.length === 0) || (!isTable && !metric);

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="data" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>

      <Field label="Display type" error={errors.chartType}>
        <Controller
          name="chartType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ChartSelect value={value} onChange={onChange} />
          )}
        />
      </Field>

      {isTable ? (
        <Field key="table" label="Table" error={errors.dataPool}>
          <Controller
            name="dataPool"
            control={control}
            defaultValue={defaultDataPool}
            render={({ field: { onChange, value } }) => (
              <DataPoolSelect
                defaultValue={defaultDataPool}
                value={value}
                onChange={(option) => {
                  handleDataPoolChange(option);
                  onChange(option);
                }}
              />
            )}
            rules={{ required: 'Please choose a table for this widget.' }}
          />
        </Field>
      ) : (
        <Field key="metric" label="Metric" error={errors.metric}>
          <Controller
            name="metric"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MetricSelect
                value={value}
                onChange={(option) => {
                  handleMetricChange(option);
                  onChange(option);
                }}
              />
            )}
            rules={{ required: 'Please choose a metric for this widget.' }}
          />
        </Field>
      )}

      {isTimeSeries && (
        <Field label="Granularity" error={errors.granularity}>
          <Controller
            name="granularity"
            control={control}
            render={({ field: { onChange, value } }) => (
              <GranularitySelect
                value={value}
                onChange={onChange}
                isOptionDisabled={(option) =>
                  isGranularityOptionDisabled(option.value, chartType)
                }
              />
            )}
            rules={{
              validate: (granularity) =>
                isGranularityOptionDisabled(granularity, chartType)
                  ? 'Please select a different granularity.'
                  : true,
            }}
          />
        </Field>
      )}

      {(isGroupedChart || isPieChart) && (
        <Field label="Group by" error={errors.dimensions}>
          {metric ? (
            <Controller
              name="dimensions"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DimensionSelect
                  metric={{ name: metric }}
                  value={value}
                  onChange={onChange}
                />
              )}
              rules={{ required: 'Please select a column.' }}
            />
          ) : (
            <Tooltip content="Please select a metric first." side="top">
              <div>
                <Select isDisabled />
              </div>
            </Tooltip>
          )}
        </Field>
      )}

      {isLeaderboard && (
        <Field label="Columns" error={errors.dimensions}>
          {metric ? (
            <Controller
              name="dimensions"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DimensionSelect
                  metric={{ name: metric }}
                  value={value}
                  onChange={onChange}
                  isMulti
                />
              )}
              rules={{ required: 'Please select at least one column.' }}
            />
          ) : (
            <Tooltip content="Please select a metric first." side="top">
              <div>
                <Select isDisabled />
              </div>
            </Tooltip>
          )}
        </Field>
      )}

      {isTable && (
        <Field label="Columns" error={errors.columns}>
          <Controller
            name="columns"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DataPoolColumnsSelect
                dataPool={dataPool}
                value={value}
                onChange={onChange}
              />
            )}
            rules={{ required: 'Please select at least one column.' }}
          />
        </Field>
      )}

      {(isLeaderboard || isPieChart) && (
        <Field label="Row limit" error={errors.rowLimit}>
          <Controller
            name="rowLimit"
            control={control}
            render={({ field: { onChange, value } }) => (
              <NumberInput
                min={1}
                step={1}
                max={1000}
                value={value}
                onChange={onChange}
                unit="rows"
              />
            )}
            rules={{
              required: 'Row limit must be at least 1.',
            }}
          />
        </Field>
      )}

      {isTable && (
        <Field label="Order by column" error={errors.orderByColumn}>
          <Controller
            name="orderByColumn"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                value={{ label: value, value }}
                defaultValue={{ label: 'timestamp', value: 'timestamp' }}
                onChange={(option) => onChange(option?.value)}
                options={DATA_POOLS[dataPool].columns.map(({ name }) => ({
                  label: name,
                  value: name,
                }))}
              />
            )}
            rules={{
              validate: (orderByColumn) => {
                const currentColumns = getValues('columns');

                if (
                  orderByColumn !== 'timestamp' &&
                  !currentColumns.includes(orderByColumn)
                ) {
                  return `${orderByColumn} needs to be part of columns.`;
                }
              },
            }}
          />
        </Field>
      )}

      {(isLeaderboard || isPieChart || isTable) && (
        <Field label="Sort" error={errors.sort}>
          <Controller
            name="sort"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                value={{ value, label: value }}
                onChange={(option) => onChange(option?.value)}
                options={SORT_OPTIONS}
              />
            )}
          />
        </Field>
      )}

      <Field label="Filters" error={errors.filters}>
        <Controller
          name="filters"
          control={control}
          disabled={dataWidgetFilterDisabled}
          render={({ field: { onChange, value } }) => (
            <DataWidgetFiltersInputComponent
              columns={getDataWidgetFilterColumns()}
              value={value}
              onChange={onChange}
              disabledTooltip={`Please select ${isTable ? 'columns' : 'a metric'} first.`}
              disabled={dataWidgetFilterDisabled}
            />
          )}
          rules={{
            validate: (filters) =>
              validateDataWidgetFilters(filters, getDataWidgetFilterColumns()),
          }}
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
